/**
 * One polling loop, written once.
 *
 * Two settings hooks watch something that finishes on the server rather than in
 * the response: a sandbox that is still validating, and a Fletcher request that
 * is still queued. Both had the same recursive function inline, each with its
 * own abort check before and after the await and its own three-way decision
 * about what a failure means. That decision is the interesting part, so it
 * stays with the caller; the loop around it lives here.
 *
 * The contract: nothing is called after the returned stop function runs, the
 * failure count resets on every answer, and a caller that returns "stop" is
 * never scheduled again.
 */

/** What a failed attempt means: wait and try once more, or give up. */
export type PollDecision = "retry" | "stop";

export interface PollOptions<T> {
  /** One attempt. The signal is aborted when the loop is stopped. */
  fetch: (signal: AbortSignal) => Promise<T>;
  /** The latest answer, delivered only while the loop is live. */
  onResult: (value: T) => void;
  /** True when the answer is terminal and there is nothing left to wait for. */
  shouldStop?: (value: T) => boolean;
  /**
   * Reports the failure and decides what happens next. `failures` counts
   * consecutive failures including this one, so the first is 1.
   */
  onError: (error: unknown, failures: number) => PollDecision;
  /** Delay before the next attempt. `failures` is 0 after an answer. */
  backoff: (failures: number) => number;
  /** Waits `backoff(0)` before the first attempt rather than going at once. */
  delayFirstAttempt?: boolean;
}

/** Starts polling. Returns the stop function an effect can hand back. */
export function startPoll<T>({
  fetch,
  onResult,
  shouldStop,
  onError,
  backoff,
  delayFirstAttempt = false,
}: PollOptions<T>): () => void {
  const controller = new AbortController();
  let timer: ReturnType<typeof setTimeout> | undefined;
  let failures = 0;

  const schedule = (delay: number) => {
    if (controller.signal.aborted) return;
    timer = setTimeout(() => void attempt(), delay);
  };

  const attempt = async () => {
    try {
      const value = await fetch(controller.signal);
      if (controller.signal.aborted) return;
      failures = 0;
      onResult(value);
      if (!shouldStop?.(value)) schedule(backoff(0));
    } catch (error) {
      if (controller.signal.aborted) return;
      failures += 1;
      if (onError(error, failures) === "retry") schedule(backoff(failures));
    }
  };

  if (delayFirstAttempt) schedule(backoff(0));
  else void attempt();

  return () => {
    controller.abort();
    clearTimeout(timer);
  };
}

/**
 * The shared shape both settings polls use: every three seconds while things
 * are healthy, then doubling to a thirty-second ceiling while they are not.
 */
export function steadyThenBackOff(failures: number): number {
  return failures ? Math.min(30000, 3000 * 2 ** failures) : 3000;
}
